import { useMemo } from "react";
import styled from "styled-components";
import { Form } from "react-bootstrap";
import { CardComp, ButtonComp } from "..";
import { formatMoney } from "../../utils/currencyFormat";
import FundingProgress from "./FundingProgress";
import RiskBadge from "./RiskBadge";
import InvestorStatusBadge from "./InvestorStatusBadge";
import { investorButtonProps } from "./investorButtonStyles";

const StickyCard = styled(CardComp)`
  && {
    position: ${(p) => (p.$embedded ? "relative" : "sticky")};
    top: ${(p) => (p.$embedded ? "auto" : "5.75rem")};
    border: none !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    border-radius: 16px;
    overflow: hidden;
    background: #fff;
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-bottom: 0 !important;
    margin-bottom: 1rem;
  }

  && .card-body {
    padding: 0;
  }

  .card-header-strip {
    background: linear-gradient(135deg, var(--color-primary) 0%, #6b252a 55%, var(--color-secondary) 100%);
    color: #fff;
    padding: 1.15rem 1.25rem;
  }

  .card-header-strip .eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.85;
    margin-bottom: 0.35rem;
  }

  .card-header-strip .title {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.35;
    margin: 0;
  }

  .card-body-inner {
    padding: 1.15rem 1.25rem 1.35rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1.25rem;
    width: 100%;
    padding: 0.65rem 0;
    border-bottom: 1px solid #f1f1f1;
    font-size: 0.875rem;

    &:last-of-type {
      border-bottom: none;
    }

    .label {
      color: #525252;
      flex: 0 0 auto;
      max-width: 55%;
      padding-right: 0.5rem;
    }

    .value {
      font-weight: 600;
      color: #383838;
      text-align: right;
      flex: 1 1 auto;
      min-width: 0;
      margin-left: auto;
    }
  }

  .total-box {
    background: rgba(139, 49, 58, 0.08);
    border: 1px solid rgba(139, 49, 58, 0.12);
    border-radius: 12px;
    padding: 1rem;
    margin: 1rem 0;
  }

  .units-input {
    background: #fafafa;
    border-color: #e0e0e0;
    border-radius: 8px;
    font-weight: 600;
    text-align: center;

    &:focus {
      border-color: var(--color-secondary);
      box-shadow: 0 0 0 0.2rem var(--color-secondary-soft);
    }
  }
`;

const MobileBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  background: #fff;
  border-top: 1px solid #e5e5e5;
  padding: 0.75rem 1rem;
  padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (min-width: 992px) {
    display: none;
  }
`;

export function InvestmentDetailStickySummary({
  opportunity,
  onInvest,
  selectedUnits,
  onUnitsChange,
  className = "",
  embedded = false,
  canInvest = true,
  unavailableNote = "",
}) {
  const estimatedTotal = useMemo(() => {
    if (!opportunity) return 0;
    return (Number(opportunity.unitPriceNgn) || 0) * (selectedUnits || opportunity.minUnitsPurchase || 1);
  }, [opportunity, selectedUnits]);

  if (!opportunity) return null;

  const units = selectedUnits ?? opportunity.minUnitsPurchase;
  const minUnits = Number(opportunity.minUnitsPurchase) || 1;
  const maxUnits = Number(opportunity.maxUnitsPurchase) || minUnits;

  return (
    <StickyCard
      $embedded={embedded}
      cardClassName={embedded ? className : `d-none d-lg-block ${className}`}
      aria-label="Investment summary"
    >
      <div className="card-header-strip">
        <p className="eyebrow mb-0">Ready to invest?</p>
        <p className="title">{opportunity.name}</p>
      </div>

      <div className="card-body-inner">
        <div className="summary-row">
          <span className="label">Expected return</span>
          <span className="value">{Number(opportunity.expectedReturnPct) || 0}%</span>
        </div>
        <div className="summary-row">
          <span className="label">Duration</span>
          <span className="value">
            {opportunity.durationMonths ? `${opportunity.durationMonths} mo` : "—"}
          </span>
        </div>
        <div className="summary-row">
          <span className="label">Unit price</span>
          <span className="value">{formatMoney(Number(opportunity.unitPriceNgn) || 0) || "—"}</span>
        </div>
        <div className="summary-row">
          <span className="label">Available units</span>
          <span className="value">
            {(Number(opportunity.availableUnits) || 0).toLocaleString()}
          </span>
        </div>

        <Form.Group className="mt-3 mb-2">
          <Form.Label className="small fw-semibold text-muted">Units</Form.Label>
          <Form.Control
            type="number"
            className="units-input"
            min={minUnits}
            max={maxUnits}
            value={units}
            onChange={(e) => onUnitsChange?.(Number(e.target.value) || minUnits)}
            aria-label="Number of units"
          />
          <Form.Text className="text-muted">
            Min {minUnits} · Max {maxUnits}
          </Form.Text>
        </Form.Group>

        <div className="total-box">
          <p className="small text-muted mb-1">Estimated total</p>
          <p className="fw-bold mb-0 fs-5" style={{ color: "var(--color-primary)" }}>
            {formatMoney(estimatedTotal)}
          </p>
        </div>

        <div className="my-3">
          <FundingProgress percent={opportunity.fundedPct} />
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <RiskBadge level={opportunity.riskLevel} />
          <InvestorStatusBadge
            status={canInvest ? "active" : "pending"}
            label={canInvest ? opportunity.opportunityStatus : "Not open"}
            backgroundColor={canInvest ? "#19bc31" : "#ff9500"}
          />
        </div>

        {canInvest ? (
          <ButtonComp btnText="Invest now" onClick={onInvest} {...investorButtonProps.primary} />
        ) : (
          <p className="small text-muted mb-0">
            {unavailableNote || "This opportunity isn’t open for investment yet."}
          </p>
        )}
      </div>
    </StickyCard>
  );
}

export function InvestmentDetailMobileBar({ opportunity, onInvest, selectedUnits }) {
  const units = selectedUnits ?? opportunity?.minUnitsPurchase ?? 1;
  const total = (opportunity?.unitPriceNgn || 0) * units;

  if (!opportunity) return null;

  return (
    <MobileBar className="investment-mobile-bar" role="region" aria-label="Invest action">
      <div className="min-w-0">
        <p className="small text-muted mb-0">{units} unit{units !== 1 ? "s" : ""}</p>
        <p className="fw-bold mb-0 text-truncate" style={{ color: "var(--color-primary)" }}>
          {formatMoney(total)}
        </p>
      </div>
      <div style={{ minWidth: 150, flex: "1 1 auto", maxWidth: 220 }}>
        <ButtonComp btnText="Invest now" onClick={onInvest} {...investorButtonProps.primary} />
      </div>
    </MobileBar>
  );
}
